import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  Auth,
  ActionCodeSettings,
  sendEmailVerification,
  confirmPasswordReset,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  applyActionCode,
  ActionCodeOperation,
  fetchSignInMethodsForEmail,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
  updatePassword,
  User,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getIdToken,
  getIdTokenResult,
  deleteUser,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  TotpSecret,
} from '@firebase/auth'
import {
  BLUEBOLT_TOTP_FACTOR_DISPLAY_NAME,
  DELETE_ACCOUNT_QUERY_PARAM,
  env,
  Errors,
  EXTEND_SESSION_QUERY_PARAM,
  GLOBALWORTH_TOTP_FACTOR_DISPLAY_NAME,
  isGlobalworth,
  paths,
  REAUTHENTICATE_QUERY_PARAM,
  SECURITY_QUERY_PARAM,
} from 'config'
import { NextFn } from 'shared/types'
import Firebase from './Firebase'
import { lookUpActionCode } from './identityToolkit'
import { AppCheck, AppCheckTokenResult, getToken } from '@firebase/app-check'
import { FirebaseApp } from '@firebase/app'
import {
  fetchAndActivate,
  getAll,
  getRemoteConfig,
} from 'firebase/remote-config'
import { isTwoFactorEnabled, OAuthProviderId } from 'shared/utils'

const TOTP_FACTOR_DISPLAY_NAME = isGlobalworth
  ? GLOBALWORTH_TOTP_FACTOR_DISPLAY_NAME
  : BLUEBOLT_TOTP_FACTOR_DISPLAY_NAME

// Shared by all instances, as they reload the same Firebase user
let pendingUserReload: Promise<void> | null = null

class FirebaseAuth {
  private instance = Firebase.getInstance()

  private app: FirebaseApp = this.instance.app

  private appCheck: AppCheck | null = this.instance.appCheck

  private auth: Auth = getAuth(this.app)

  private remoteConfig = getRemoteConfig(this.app)

  confirmPasswordReset = async (code: string, password: string) => {
    await confirmPasswordReset(this.auth, code, password)
  }

  verifyPasswordResetCode = async (code: string) => {
    await verifyPasswordResetCode(this.auth, code)
  }

  sendPasswordResetEmail = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: env.appUrl,
    }
    await sendPasswordResetEmail(this.auth, email, actionCodeSettings)
  }

  activateAccount = async (code: string) => {
    return applyActionCode(this.auth, code)
  }

  checkRevertSecondFactorCode = async (code: string) => {
    const appCheckToken = await this.getAppCheckToken()
      ?.then(({ token }) => token)
      .catch(() => undefined)
    const { requestType, email, mfaInfo } = await lookUpActionCode(
      this.app.options.apiKey ?? '',
      code,
      appCheckToken
    )
    if (requestType !== ActionCodeOperation.REVERT_SECOND_FACTOR_ADDITION) {
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.InvalidActionCode }
    }
    // The factor from the email is no longer on the account, e.g. 2FA has been
    // enabled again since in the other app
    if (!mfaInfo) {
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.ExpiredActionCode }
    }
    return {
      email: email ?? null,
      factorDisplayName: mfaInfo.displayName ?? null,
    }
  }

  revertSecondFactorAddition = async (code: string) => {
    await applyActionCode(this.auth, code)
    // Firebase ends the account's sessions on every device. Reloading makes it
    // take effect here at once - the SDK signs out on the expired token error.
    await this.auth.currentUser?.reload().catch(() => undefined)
  }

  signIn = async (email: string, password: string) => {
    const response = await signInWithEmailAndPassword(
      this.auth,
      email,
      password
    )
    if (!response.user.emailVerified) {
      const actionCodeSettings: ActionCodeSettings = {
        url: env.appUrl,
        handleCodeInApp: true,
      }
      try {
        await sendEmailVerification(response.user, actionCodeSettings)
      } finally {
        // An unverified user must not stay signed in, even if sending fails
        // (e.g. too many requests)
        await this.auth.signOut()
      }
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.AccountNotActivated }
    }
  }

  signInWithEmailLink = async (email: string, emailLink: string) =>
    await signInWithEmailLink(this.auth, email, emailLink)

  private getOAuthProvider = (providerId: OAuthProviderId) => {
    const provider = new OAuthProvider(providerId)
    provider.setCustomParameters({
      prompt: 'select_account',
    })
    provider.addScope('openid')
    provider.addScope('email')
    provider.addScope('profile')
    return provider
  }

  signInWithOAuthProvider = async (providerId: OAuthProviderId) => {
    const result = await signInWithPopup(
      this.auth,
      this.getOAuthProvider(providerId)
    )
    return result.user
  }

  reauthenticateWithOAuthProvider = async (providerId: OAuthProviderId) =>
    reauthenticateWithPopup(
      this.getSignedInUser(),
      this.getOAuthProvider(providerId)
    )

  getCurrentUser = () => this.auth.currentUser

  signOut = () => signOut(this.auth)

  onAuthStateChanged = (next: NextFn<User | null>) =>
    onAuthStateChanged(this.auth, async user => next(user))

  // Also called when another tab of the same browser changes the session
  onIdTokenChanged = (next: NextFn<User | null>) =>
    onIdTokenChanged(this.auth, async user => next(user))

  fetchSignInMethodsForEmail = async (email: string) =>
    await fetchSignInMethodsForEmail(this.auth, email)

  sendSignInLinkToEmail = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: env.appUrl,
      handleCodeInApp: true,
    }
    return await sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
  }

  sendSignInLinkToEmailToReauthBeforeSecurityChange = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${env.appUrl}${paths.signInWithLink}?${REAUTHENTICATE_QUERY_PARAM}=true&${SECURITY_QUERY_PARAM}=true`,
      handleCodeInApp: true,
    }
    return await sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
  }

  sendSignInLinkToEmailToReauthBeforeSessionExtension = async (
    email: string
  ) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${env.appUrl}${paths.signInWithLink}?${REAUTHENTICATE_QUERY_PARAM}=true&${EXTEND_SESSION_QUERY_PARAM}=true`,
      handleCodeInApp: true,
    }
    return await sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
  }

  sendSignInLinkToEmailToReauthBeforDeleteAccount = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${env.appUrl}?${REAUTHENTICATE_QUERY_PARAM}=true&${DELETE_ACCOUNT_QUERY_PARAM}=true`,
      handleCodeInApp: true,
    }
    return await sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
  }

  // The link is opened in another tab, the reauthentication dialog waits for it
  sendSignInLinkToEmailToReauthenticate = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${env.appUrl}${paths.reauthenticateWithLink}`,
      handleCodeInApp: true,
    }
    return await sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
  }

  sendSignInLinkToEmailToLoginBeforDeleteAccount = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${env.appUrl}?${DELETE_ACCOUNT_QUERY_PARAM}=true`,
      handleCodeInApp: true,
    }
    return await sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
  }

  isSignInWithEmailLink = (emailLink: string) =>
    isSignInWithEmailLink(this.auth, emailLink)

  getAppCheckToken = (): Promise<AppCheckTokenResult> | undefined => {
    if (this.appCheck === null) return
    return getToken(this.appCheck)
  }
  reloadUser = async () => {
    await this.auth.currentUser?.reload()
  }

  updatePassword = async (newPassword: string) => {
    if (!this.auth.currentUser) return
    return updatePassword(this.auth.currentUser, newPassword)
  }

  reauthenticateWithPasswordCredential = async (
    email: string,
    password: string
  ) => {
    const credential = EmailAuthProvider.credential(email, password)
    return reauthenticateWithCredential(this.getSignedInUser(), credential)
  }

  reauthenticateWithEmailLinkCredential = async (
    email: string,
    emailLink: string
  ) => {
    const credential = EmailAuthProvider.credentialWithLink(email, emailLink)
    return reauthenticateWithCredential(this.getSignedInUser(), credential)
  }

  getIdToken = async () => {
    if (!this.auth.currentUser) return
    return getIdToken(this.auth.currentUser, false)
  }

  // Time of the last sign-in or reauthentication, issued by the server
  getAuthTime = async () => {
    if (!this.auth.currentUser) return
    const { authTime } = await getIdTokenResult(this.auth.currentUser)
    return authTime
  }

  getMultiFactorResolver = (error: MultiFactorError) =>
    getMultiFactorResolver(this.auth, error)

  resolveTotpSignIn = async (resolver: MultiFactorResolver, code: string) => {
    const totpHint = resolver.hints.find(
      ({ factorId }) => factorId === TotpMultiFactorGenerator.FACTOR_ID
    )
    if (!totpHint) {
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.MultiFactorInfoNotFound }
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
      totpHint.uid,
      code
    )
    const credential = await resolver.resolveSignIn(assertion)
    // A reauthentication resolved after a sign-out, e.g. in another tab, signs
    // no one in
    this.getSignedInUser()
    return credential
  }

  // Reauthentication and 2FA changes fail without a user, so they can't look
  // successful by doing nothing
  private getSignedInUser = () => {
    if (!this.auth.currentUser) {
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.NullUser }
    }
    return this.auth.currentUser
  }

  // Reloads running at once would read the factors cleared by each other, so a
  // call made during a reload waits for that one
  reloadUserWithTokenRefresh = () => {
    if (!pendingUserReload) {
      pendingUserReload = this.refreshTokenAndReloadUser().finally(() => {
        pendingUserReload = null
      })
    }
    return pendingUserReload
  }

  // Sessions ended on another device, e.g. by disabling 2FA there, show up only
  // on a token refresh, and the SDK signs out then
  private refreshTokenAndReloadUser = async () => {
    const user = this.getSignedInUser()
    await user.getIdToken(true)
    await this.reloadUserWithFactors(user)
  }

  // On reload the SDK replaces the second factors only if the account still has
  // some, so the last one removed on another device would stay. Cleared first,
  // they come back from the server only if they still exist.
  private reloadUserWithFactors = async (user: User) => {
    const multiFactorUser = multiFactor(user) as {
      enrolledFactors: MultiFactorInfo[]
    }
    const { enrolledFactors } = multiFactorUser
    multiFactorUser.enrolledFactors = []
    try {
      await user.reload()
    } catch (error) {
      multiFactorUser.enrolledFactors = enrolledFactors
      throw error
    }
  }

  generateTotpSecret = async () => {
    const user = this.getSignedInUser()
    await user.reload()
    if (isTwoFactorEnabled(user)) {
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.SecondFactorLimitExceeded }
    }
    const session = await multiFactor(user).getSession()
    return TotpMultiFactorGenerator.generateSecret(session)
  }

  enrollTotp = async (secret: TotpSecret, code: string) => {
    const user = this.getSignedInUser()
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      secret,
      code
    )
    try {
      await multiFactor(user).enroll(assertion, TOTP_FACTOR_DISPLAY_NAME)
    } catch (error: any) {
      if (error?.code === Errors.SecondFactorLimitExceeded) {
        await user.reload().catch(() => undefined)
      }
      throw error
    }
  }

  unenrollTotp = async () => {
    const user = multiFactor(this.getSignedInUser())
    const totpFactor = user.enrolledFactors.find(
      ({ factorId }) => factorId === TotpMultiFactorGenerator.FACTOR_ID
    )
    if (!totpFactor) {
      // eslint-disable-next-line no-throw-literal
      throw { code: Errors.MultiFactorInfoNotFound }
    }
    await user.unenroll(totpFactor)
  }

  deleteUser = async () => {
    if (!this.auth.currentUser) return
    try {
      await deleteUser(this.auth.currentUser)
    } catch (error: any) {
      throw error
    }
  }

  getRemoteConfigValues = async () => {
    this.remoteConfig.settings.minimumFetchIntervalMillis = 86_400_00

    this.remoteConfig.defaultConfig = {
      globalworth_en_tutorial_url:
        'https://files.globalworth.app/Guidebook_EN.pdf',
      bluebolt_en_regulations_url:
        'https://www.bluebolt.pl/files/regulations.html',
      globalworth_help_phone_number: '+48 608 103 000',
      globalworth_help_email: 'support@globalworth.app',
      bluebolt_en_tutorial_url: 'https://www.bluebolt.pl/files/manual.html',
      bluebolt_help_phone_number: '+48 501 553 379',
      globalworth_privacy_policy_url:
        'https://www.bluebolt.pl/files/privacypolicy.html',
      bluebolt_pl_tutorial_url: 'https://www.bluebolt.pl/files/instrukcja.html',
      bluebolt_pl_regulations_url:
        'https://www.bluebolt.pl/files/regulamin.html',
      bluebolt_privacy_policy_url:
        'https://www.bluebolt.pl/files/privacypolicy.html',
      globalworth_en_regulations_url:
        'https://www.bluebolt.pl/files/regulations.html',
      bluebolt_help_email: 'support@bluebolt.pl',
      globalworth_pl_regulations_url:
        'https://www.bluebolt.pl/files/regulamin.html',
      globalworth_pl_tutorial_url:
        'https://files.globalworth.app/Guidebook_PL.pdf',
    }

    try {
      await fetchAndActivate(this.remoteConfig)
      const values = getAll(this.remoteConfig)
      const configsObject = Object.fromEntries(
        Object.entries(values).map(([key, config]) => [key, config.asString()])
      )
      return configsObject
    } catch (err) {
      console.error(err)
    }
  }
}

export default FirebaseAuth
